import { ensure, escapeHtml, Five, generateId, isNone, isSome, keepExisting, objectKeys } from '../util.js';
import { Maybe, Timestamp } from '../data.js';
import { ExpandedCart } from './cart.js';
import { DomainId } from './domains.js';
import { Menu } from './menus.js';
import { ComputedProduct, ProductCategory } from './products.js';
import { Order, OrderItem } from './orders.js';
import { Tier, TierCategory, TierCategoryGroup } from './stores.js';
import { MessageTemplateVariables } from './messages.js';
import { CSSProperties } from 'preact/compat';

export interface Page {
	id: string & { __entity?: 'Page' };
	domain: DomainId;
	title: string;
	url?: Maybe<string>;
	content: ContentItem;
	visible: boolean;
	index: boolean;
	description: Maybe<string>;
	keywords: Maybe<string>;
	meta_image: Maybe<string>;
	redirect: Maybe<string>;
	created: Timestamp;
	updated: Timestamp;
}

export interface Blog {
	id: string & { __entity?: 'Blog' };
	title: string;
	url?: Maybe<string>;
	categories: string[];
	author?: Maybe<string>;
	abstract: Maybe<ContentItem>;
	content: ContentItem;
	domain: DomainId;
	visible: boolean;
	keywords?: Maybe<string>;
	meta_description?: Maybe<string>;
	image?: Maybe<string>;
	posted: Timestamp;
	post_updated: Maybe<Timestamp>;
	redirect: Maybe<string>;
	created: Timestamp;
	updated: Timestamp;
}

export interface BlogCategory {
	id: string & { __entity?: 'BlogCategory' };
	domain: Maybe<DomainId>;
	url: Maybe<string>;
	name: string;
	visible: boolean;
	main: boolean;
	created: Timestamp;
	updated: Timestamp;
}

export interface Block {
	id: string & { __entity?: 'Block' };
	domain: DomainId;
	key: string;
	unwrap: boolean;
	content: ContentItem;
	created: Timestamp;
	updated: Timestamp;
}

export interface ContentExtras {
	styleTagNames: Record<string, Maybe<ContentTagName>>;
	blocks: Record<string, Maybe<Block>>;
	orders: Record<number, Order>;
	orderItems: Record<number, OrderItem>;
	productSets: Record<string, ComputedProduct[]>;
	cart: Maybe<ExpandedCart>;
	menus: Record<string, Menu>;
	homePageBlogPosts: Maybe<Blog[]>;
	tiers: Maybe<{
		tiers: Tier[];
		groups: TierCategoryGroup[];
		categories: TierCategory[];
	}>;
}

export interface ContentWithExtras {
	content: ContentItem;
	extras: ContentExtras;
}

export interface ContentHeaderImgDisplay {
	size?: Maybe<string>;
	backgroundColor?: Maybe<string>;
	horizontal?: Maybe<string>;
	vertical?: Maybe<string>;
}

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export type ContentCondition = (keyof MessageTemplateVariables | string);

export type ContentTagName = keyof HTMLElementTagNameMap;

export interface ContentItemBase {
	id: string & { __entity?: 'ContentItem' };
	reference?: Maybe<string>;
	type: ContentItemType;
	when?: Maybe<ContentCondition[]>;
	span?: number;
	style?: Maybe<string | string[]>;
	gridColumns?: number;
	backgroundColor?: Maybe<string>;
	fontColor?: Maybe<string>;
	children?: ContentItem[];
	css?: CSSProperties;
	html?: string;
	tagName?: Maybe<ContentTagName>;
	innerCSS?: Record<string, Record<string, string>>;
}

export interface BasicContentItem<T extends ContentItemType> extends ContentItemBase {
	type: T;
}

export const containerItemTypes = ['rows', 'columns', 'grid'] as const;
export type ContainerItemType = typeof containerItemTypes[number];

export interface ContentContainer extends ContentItemBase {
	type: ContainerItemType;
	children: ContentItem[];
}

export interface ContentHTMLItem extends ContentItemBase {
	type: 'html';
	html: string;
}

export interface ContentBlockItem extends ContentItemBase {
	type: 'block';
	key: Maybe<string>;
	tagName?: never;
}

export interface ContentImageItem extends ContentItemBase {
	type: 'image';
	src?: Maybe<string>;
	info?: Maybe<string>;
	link?: Maybe<string>;
	width?: Maybe<string>;
	height?: Maybe<string>;
	imageWidth?: number;
	imageHeight?: number;
	alt?: Maybe<string>;
	ariaHidden?: boolean;
	lazy?: boolean;
	innerCSS?: Partial<Record<'a' | 'img' | 'info', Record<string, string>>>;
	tagName?: never;
}

export interface ContentBannerItem extends ContentItemBase {
	type: 'banner';
	src?: Maybe<string>;
	info?: Maybe<string>;
	link?: Maybe<string>;
	width?: Maybe<string>;
	height?: Maybe<string>;
	textShadowX?: Maybe<string>;
	textShadowY?: Maybe<string>;
	textShadowBlur?: Maybe<string>;
	textShadowColor?: Maybe<string>;
	alt?: Maybe<string>;
	innerCSS?: Partial<Record<'a' | 'img' | 'overlay', Record<string, string>>>;
}

export interface ContentProductSetBlockItem extends ContentItemBase {
	type: 'productset';
	categories: ProductCategory['id'][];
}

export interface ContentMenuBlockItem extends ContentItemBase {
	type: 'menu';
	menuId: Menu['id'];
}

export interface ContentGridItem extends ContentContainer {
	type: 'grid';
	columns: number;
}

export interface ContentButtonItem extends ContentItemBase {
	type: 'button';
	info: string;
	link: string;
	/** @deprecated Use style instead. */
	variant: Maybe<string>;
	innerCSS?: Partial<Record<'a', Record<string, string>>>;
	tagName?: never;
}

export interface ContentHomePageBlogPostItem extends ContentItemBase {
	type: 'homePageBlogPost';
}

export interface ContentCommissionsBlockItem extends ContentItemBase {
	type: 'commissions';
	variant: 'commissions' | 'affiliate' | 'sponsorship';
}

export const imageContentStyles = ['left', 'center', 'right', 'full-width'];

export interface ContentItemTypeMap {

	init: BasicContentItem<'init'>;

	rows: ContentContainer;
	columns: ContentContainer;
	html: ContentHTMLItem;
	image: ContentImageItem;
	grid: ContentGridItem;

	button: ContentButtonItem;
	banner: ContentBannerItem;
	menu: ContentMenuBlockItem;
	block: ContentBlockItem;

	productset: ContentProductSetBlockItem;

	size_chart: BasicContentItem<'size_chart'>;
	prod_time: BasicContentItem<'prod_time'>;

	homePageBlogPost: ContentHomePageBlogPostItem;
	commissions: ContentCommissionsBlockItem;

}
export type ContentItemType = keyof ContentItemTypeMap;
export type ContentItem = ContentItemTypeMap[ContentItemType];

export type MakeContentProps<T> = Omit<T, 'id' | 'type'> & { id?: Maybe<string> };

export const makeContent = <K extends ContentItemType, T extends ContentItemTypeMap[K]> (type: K, props: MakeContentProps<T>) => ({
	id: props.id || generateId(),
	type: type as T['type'],
	...props,
} as T);

export const isContent = <
	T extends ContentItemTypeMap[K],
	K extends ContentItemType = 'init'
> (item: any, type?: K): item is (T & { type: K }) => (
	item && typeof item == 'object' && item.id && (!type || item.type == type)
);

export const isContentContainer = (item: any): item is ContentContainer => (
	item && typeof item == 'object' && Array.isArray(item.children)
);

export type VariableMissingOption = 'throw' | 'empty' | 'leave' | 'check';
export interface FillInContentOptions {
	variables: Record<string, any>,
	escape: Maybe<string[]>,
	/**
	 * How to handle missing variables.
	 * - throw - default
	 * - empty - replace with empty string
	 * - leave - replace {} with [] (kludge) and otherwise leave unchanged
	 * - check - fillInContentVariables returns `Promise<true>` if there are no missing variables (after applying conditionals) (actual result is discarded)
	 */
	missing?: VariableMissingOption,
}

export async function fillInContentVariables<T extends ContentItem | string>(content: T, options: FillInContentOptions & { missing: 'check' }): Promise<boolean>;
export async function fillInContentVariables<T extends ContentItem | string>(content: T, options: FillInContentOptions & { missing?: Omit<VariableMissingOption, 'check'> }): Promise<T>;
export async function fillInContentVariables<T extends ContentItem | string>(
	content: T,
	{ variables, escape, missing = 'throw' }: FillInContentOptions
) {

	const normVars = Object.fromEntries(Object.entries(variables).map(([k, v]) => [k.toLowerCase(), v]));
	const normEscape = escape?.map(s => s.toLowerCase());
	let failed = false;

	const fillInString = async (input: string) => {
		let str = input;
		while (true) {

			const match = str.match(/\{(!?)(\w+)([?=]?)?(\w*?)?}/);

			if (!match) break;
			const [full, neg, name, cond, cmp] = [...match] as Five<string>;
			const normName = name.toLowerCase();
			let value = normVars[normName];
			if (typeof value == 'function') value = await value();
			
			if (cond) {
				const endStr = `{/${neg}${name}${cond}${cmp || ''}}`;
				const end = str.indexOf(endStr, match.index);
				if (end < 0) throw new Error(`Expected "${endStr}", not found`);
				const keep = (cond == '=' ? value == cmp : (isSome(value) && value !== false)) == !neg;
				const repl = keep
					? str.slice(match.index! + full.length, end)
					: '';
				str = str.slice(0, ensure(match.index)) + repl + str.slice(end + endStr.length);
			}
			else {
				if (isNone(value)) {
					failed = true;
					if (missing == 'throw') {
						throw new Error(`Variable not found: ${name}`);
					}
					else if (missing == 'leave') {
						str = str.slice(0, ensure(match.index)) + '[' + full.slice(1, -1) + ']' + str.slice(ensure(match.index) + full.length);
						continue;
					}
					else {
						value = '';
					}
				}
				let ins = String(value);
				if (normEscape?.includes(normName)) ins = escapeHtml(ins);
				str = str.slice(0, ensure(match.index)) + ins + str.slice(ensure(match.index) + full.length);
			}

		}
		return str;
	};

	if (typeof content == 'string') {
		const res = await fillInString(content) as T;
		if (missing == 'check') return !failed;
		return res;
	}

	const recObj = async (it: object) => {
		for (const key of objectKeys(it)) {
			if (typeof it[key] as any == 'object') await recObj(it[key] as any);
			else if (typeof it[key] as any == 'string') (it[key] as string) = await fillInString(it[key] as any);
		}
	};

	const rec = async (item: ContentItem): Promise<ContentItem> => {
		item = { ...item };
		if (isContentContainer(item)) {
			item.children = keepExisting(await Promise.all(item.children.map(async child => {
				if (child.when) for (const condStr of child.when) {
					const match = condStr.match(/^(!?)(\w+)([?=]?)(.*?)?$/);
					if (!match) throw new Error('Invalid condition string');
					const [full, neg, name, cond, val] = [...match] as Five<string>;
					const normName = name.toLowerCase();
					let value = normVars[normName];
					if (typeof value == 'function') value = await value();
					if (cond == '=' && value != val) return null;
					if ((isSome(value) && value !== false) == !!neg) return null;
				}
				return await rec(child);
			})));
		}
		for (const key of objectKeys(item)) {
			if (key == 'children' || key == 'when') continue;
			else if (typeof item[key] as any == 'object') await recObj(item[key] as any);
			else if (typeof item[key] as any == 'string') (item[key] as string) = await fillInString(item[key] as any);
		}
		return item;
	};

	if (content) content = await rec(Array.isArray(content) ? { children: keepExisting(content) } as ContentItem : content) as T;
	
	if (missing == 'check') return !failed;

	return content;

}

export function stringHasVariable(str: string, name: string) {
	const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	return !!str.match(new RegExp(`\\{!?${esc}[?=}]`));
}
